import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as SliderPrimitive from '@radix-ui/react-slider';
import {
  getFaceRecognitionSettings,
  updateFaceRecognitionSettings,
  FaceRecognitionSettingsView,
  FaceRecognitionSettingsInput,
} from '../../../api/Attendance/faceRecognitionSettings';
import { useFaceRecognitionSettingsSignalR } from '../../../hooks';
import { Card, CardHeader, CardContent } from '../../../components/ui/Card';
import { Switch } from '../../../components/ui/Switch';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Spinner } from '../../../components/ui/Spinner';
import AppIcon from '../../../components/AppIcon';
import { PageLayout } from '../../../components/ui/PageLayout';
import { ScanFace } from 'lucide-react';
import { useToast, ToastRenderer } from '../../../hooks/useToast';

export default function FaceRecognitionSettings() {
  const { t } = useTranslation();
  const { toast, showToast, dismissToast } = useToast();

  const [settings, setSettings] = useState<FaceRecognitionSettingsView | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { settings: signalRSettings } = useFaceRecognitionSettingsSignalR();

  const [formState, setFormState] = useState<FaceRecognitionSettingsInput>({
    faceMatchThreshold: 0.5,
    livenessThreshold: 0.3,
    debounceFrames: 12,
    recognitionTimeoutSeconds: 30,
    globalFaceRecognitionEnabled: true,
    requireLivenessCheck: true,
  });

  useEffect(() => {
    loadSettings();
  }, []);

  // Sync form state when SignalR updates arrive (only if user hasn't made changes)
  useEffect(() => {
    if (signalRSettings && !hasChanges) {
      setFormState({
        faceMatchThreshold: signalRSettings.faceMatchThreshold,
        livenessThreshold: signalRSettings.livenessThreshold,
        debounceFrames: signalRSettings.debounceFrames,
        recognitionTimeoutSeconds: signalRSettings.recognitionTimeoutSeconds,
        globalFaceRecognitionEnabled: signalRSettings.globalFaceRecognitionEnabled,
        requireLivenessCheck: signalRSettings.requireLivenessCheck,
      });
    }
  }, [signalRSettings, hasChanges]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await getFaceRecognitionSettings();
      if (result.status === 200 && result.payload) {
        setSettings(result.payload);
        setFormState({
          faceMatchThreshold: result.payload.faceMatchThreshold,
          livenessThreshold: result.payload.livenessThreshold,
          debounceFrames: result.payload.debounceFrames,
          recognitionTimeoutSeconds: result.payload.recognitionTimeoutSeconds,
          globalFaceRecognitionEnabled: result.payload.globalFaceRecognitionEnabled,
          requireLivenessCheck: result.payload.requireLivenessCheck,
        });
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Failed to load face recognition settings:', error);
      showToast(t('Failed to load settings'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const result = await updateFaceRecognitionSettings(formState);
      if (result.status === 200 && result.payload) {
        setSettings(result.payload);
        setHasChanges(false);
        showToast(t('Settings saved successfully'), 'success');
      } else {
        showToast(t('Failed to save settings'), 'error');
      }
    } catch (error) {
      console.error('Failed to save face recognition settings:', error);
      showToast(t('Failed to save settings'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFormChange = (field: keyof FaceRecognitionSettingsInput, value: any) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleReset = () => {
    if (settings) {
      setFormState({
        faceMatchThreshold: settings.faceMatchThreshold,
        livenessThreshold: settings.livenessThreshold,
        debounceFrames: settings.debounceFrames,
        recognitionTimeoutSeconds: settings.recognitionTimeoutSeconds,
        globalFaceRecognitionEnabled: settings.globalFaceRecognitionEnabled,
        requireLivenessCheck: settings.requireLivenessCheck,
      });
      setHasChanges(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('Never');
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <PageLayout
      title={t('Face Recognition Settings')}
      description={t('Configure system-wide face recognition thresholds and behavior. These settings affect all users and companies.')}
      icon={ScanFace}
      accentColor="blue"
    >
      <div className="space-y-6">

        {/* Audit info */}
        {settings && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm text-foreground">
            <AppIcon name="Info" size={16} className="inline me-2 text-primary" />
            {t('Last modified by')}: <strong>{settings.lastModifiedBy || t('System')}</strong> {t('on')} {formatDate(settings.lastModifiedAt)}
          </div>
        )}

        {/* Feature Toggles */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-foreground">{t('Feature Toggles')}</h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <Switch
              checked={formState.globalFaceRecognitionEnabled}
              onCheckedChange={(checked) => handleFormChange('globalFaceRecognitionEnabled', checked)}
              label={t('Global Face Recognition Enabled')}
              description={t('Master switch to enable/disable face recognition system-wide')}
            />
            <Switch
              checked={formState.requireLivenessCheck}
              onCheckedChange={(checked) => handleFormChange('requireLivenessCheck', checked)}
              disabled={!formState.globalFaceRecognitionEnabled}
              label={t('Require Liveness Check')}
              description={t('Enable anti-spoofing detection to prevent photo/video attacks')}
            />
          </CardContent>
        </Card>

        {/* Thresholds */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-foreground">{t('Recognition Thresholds')}</h2>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Face Match Threshold */}
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                {t('Face Match Threshold')}: <strong>{formState.faceMatchThreshold.toFixed(2)}</strong>
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                {t('Minimum similarity score (0-1) for a face to be considered a match. Higher = stricter.')}
              </p>
              <SliderPrimitive.Root
                className="relative flex w-full touch-none select-none items-center"
                value={[formState.faceMatchThreshold]}
                onValueChange={([v]) => handleFormChange('faceMatchThreshold', v)}
                min={0.1}
                max={0.9}
                step={0.05}
                disabled={!formState.globalFaceRecognitionEnabled}
              >
                <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-muted">
                  <SliderPrimitive.Range className="absolute h-full bg-primary" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-card ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
              </SliderPrimitive.Root>
              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                <span>0.1</span>
                <span>{t('Lenient')}</span>
                <span>{t('Default')}</span>
                <span>{t('Strict')}</span>
                <span>0.9</span>
              </div>
            </div>

            {/* Liveness Threshold */}
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                {t('Liveness Threshold')}: <strong>{formState.livenessThreshold.toFixed(2)}</strong>
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                {t('Minimum liveness score (0-1) to pass anti-spoofing check. Higher = stricter.')}
              </p>
              <SliderPrimitive.Root
                className="relative flex w-full touch-none select-none items-center"
                value={[formState.livenessThreshold]}
                onValueChange={([v]) => handleFormChange('livenessThreshold', v)}
                min={0.1}
                max={0.8}
                step={0.05}
                disabled={!formState.globalFaceRecognitionEnabled || !formState.requireLivenessCheck}
              >
                <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-muted">
                  <SliderPrimitive.Range className="absolute h-full bg-primary" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-card ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
              </SliderPrimitive.Root>
              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                <span>0.1</span>
                <span>{t('Lenient')}</span>
                <span>{t('Default')}</span>
                <span>{t('Strict')}</span>
                <span>0.8</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* UI Behavior */}
        <Card>
          <CardHeader>
            <h2 className="text-lg font-semibold text-foreground">{t('UI Behavior')}</h2>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Debounce Frames */}
            <div>
              <p className="text-sm font-medium text-foreground mb-1">
                {t('Debounce Frames')}: <strong>{formState.debounceFrames}</strong>
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                {t('Number of frames before status updates. Prevents rapid flickering. At 30fps, 12 frames ≈ 400ms.')}
              </p>
              <SliderPrimitive.Root
                className="relative flex w-full touch-none select-none items-center"
                value={[formState.debounceFrames]}
                onValueChange={([v]) => handleFormChange('debounceFrames', v)}
                min={1}
                max={30}
                step={1}
                disabled={!formState.globalFaceRecognitionEnabled}
              >
                <SliderPrimitive.Track className="relative h-2 w-full grow overflow-hidden rounded-full bg-muted">
                  <SliderPrimitive.Range className="absolute h-full bg-primary" />
                </SliderPrimitive.Track>
                <SliderPrimitive.Thumb className="block h-5 w-5 rounded-full border-2 border-primary bg-card ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" />
              </SliderPrimitive.Root>
              <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                <span>1</span>
                <span>6</span>
                <span>{t('Default')}</span>
                <span>20</span>
                <span>30</span>
              </div>
            </div>

            {/* Recognition Timeout */}
            <Input
              label={t('Recognition Timeout (seconds)')}
              type="number"
              value={formState.recognitionTimeoutSeconds}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                handleFormChange(
                  'recognitionTimeoutSeconds',
                  Math.min(60, Math.max(5, parseInt(e.target.value) || 5))
                )
              }
              min={5}
              max={60}
              description={t('Maximum seconds to wait for face recognition (5-60)')}
              disabled={!formState.globalFaceRecognitionEnabled}
            />
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="sticky bottom-0 flex items-center justify-end gap-3 rounded-lg border border-border bg-card p-4">
          <Button
            variant="outline"
            iconName="RefreshCw"
            onClick={handleReset}
            disabled={!hasChanges || saving}
          >
            {t('Reset')}
          </Button>
          <Button
            iconName={saving ? undefined : 'Save'}
            loading={saving}
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? t('Saving...') : t('Save Changes')}
          </Button>
        </div>
      </div>

      <ToastRenderer toast={toast} onDismiss={dismissToast} />
    </PageLayout>
  );
}
